import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { TransferNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Shared hook for transfer node functionality.
 * Handles extension and timeout updates for transfer nodes.
 */
export const useTransferNode = (id: string, data: TransferNodeData) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const currentExtensionId = data.extensionId ? String(data.extensionId) : ''

  // Local state for timeout input (to prevent focus loss)
  const [timeout, setTimeoutValue] = useState(
    data.timeout !== undefined ? String(data.timeout) : '',
  )

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setTimeoutValue(data.timeout !== undefined ? String(data.timeout) : '')
    }
  }, [data.timeout])

  const handleExtensionChange = useCallback(
    (extensionId: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        extensionId,
      })
    },
    [id, updateNodeData],
  )

  const handleTimeoutChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setTimeoutValue(value)
  }, [])

  const handleTimeoutBlur = useCallback(() => {
    const timeoutValue = timeout ? parseInt(timeout, 10) : undefined
    // Clamp to valid range: 5-300
    const clampedValue =
      timeoutValue && !isNaN(timeoutValue)
        ? Math.max(5, Math.min(300, timeoutValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      timeout: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, timeout])

  return {
    currentExtensionId,
    timeout,
    handleExtensionChange,
    handleTimeoutChange,
    handleTimeoutBlur,
    hasData: !!currentExtensionId,
  }
}
